import React, { useEffect, useState } from 'react'
import { formatDate } from '@/common/time'
import { MachineName } from '@/components/machine'
import { Order, OrderManager } from '@/components/order/Order'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { SearchCriteria, SearchCriteriaParams } from '@/pages/communal/statistics/components/common/searchCriteria'
import { MachineType } from '@/services/model/machine'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useNavigate } from 'react-router-dom'

interface FailureCounterPopupProps {
  services: Services
  session: CurrentSession
  criteria: SearchCriteria
  bucketFor: Date
  goToMachinePage: (machineId: string) => void
  onClose: () => void
  endpoint?: 'failures' | 'new-failures' | 'resolved-failures'
}

interface OpenFailure {
  id: string
  category: string | null
  description: string | null
  detectionDate: string | null
  commencementDate?: string | null
  createdAt: string
  machine: {
    id: string
    imei: string
    internalNumber: string
    type: MachineType
  }
}

const FailureCounterPopupOrderCols = ['machine.internalNumber', 'detectionDate', 'commencementDate', 'category', 'description']

export const FailureCounterPopupBody = ({ services, session, criteria, bucketFor, goToMachinePage, onClose, endpoint = 'failures' }: FailureCounterPopupProps) => {
  const navigate = useNavigate()
  const bucketForTime = bucketFor.getTime()
  const dateKey = endpoint === 'resolved-failures' ? 'commencementDate' : 'detectionDate'
  const [response, setResponse] = useState<OpenFailure[]>()
  const [listState, setListState] = useState<StaticPaginatedTableState>({ pageNumber: 1, pageSize: 10 })
  const [orderManager, setOrderManager] = useState<OrderManager>(() => new OrderManager(
    FailureCounterPopupOrderCols,
    { key: dateKey, asc: false },
    (order: Order) => {
      setOrderManager((current) => current.newManager(order))
      setListState((current) => ({ ...current, pageNumber: 1 }))
    }
  ))

  useEffect(() => {
    setResponse(undefined)
    services.session
      .fetchWithToken(`/api/communal/statistics/machines/bucket/${endpoint}?bucketFor=${new Date(bucketForTime).toISOString()}&${SearchCriteriaParams(criteria)}`)
      .then((r) => r.json())
      .then(setResponse)
  }, [services.session, criteria, bucketForTime, endpoint])

  if (!response) {
    return <div className='spinner'></div>
  }

  const headerRow = (key: string, translationKey: string) => <div className='hcol' onClick={() => orderManager.click(key)}>
    {session.translates.for(translationKey)}
    {orderManager.orderInfoElement(key)}
  </div>

  const goToMachineFailures = (machineId: string) => {
    navigate(`/communal/machine/${machineId}/failures`)
    onClose()
  }

  return <div>
    <h3>{formatDate(bucketFor)}</h3>
    <StaticPaginatedTable<OpenFailure>
      data={orderManager.orderListByKey(response)}
      parentState={listState}
      setParentState={setListState}
      header={() => <>
        {headerRow('machine.internalNumber', 'elements.table.maxhitpop.machine')}
        {headerRow(dateKey, endpoint === 'resolved-failures' ? 'page.machinefailures.commencementRow' : 'page.machinefailures.reportingRow')}
        {headerRow('category', 'elements.table.machines.type_category')}
        {headerRow('description', 'page.machinefailures.descriptionTitle')}
      </>}
      row={(row) => <>
        <div className='col' onClick={() => {
          goToMachinePage(row.machine.id)
          onClose()
        }}><MachineName row={row.machine} /></div>
        <div className='col' onClick={() => goToMachineFailures(row.machine.id)}>{row[dateKey] ? formatDate(new Date(row[dateKey])) : '-'}</div>
        <div className='col' onClick={() => goToMachineFailures(row.machine.id)}>{row.category ? session.translates.for(`failures.category.${row.category}`) : '-'}</div>
        <div className='col' onClick={() => goToMachineFailures(row.machine.id)}>{row.description || '-'}</div>
      </>}
      translates={session.translates}
    />
  </div>
}
